import { z } from "zod";

/**
 * Contratos del import masivo de planta (ADR 055). El ADMIN sube un Excel (lo
 * parsea el front) con docentes o estudiantes; cada fila CREA la cuenta INACTIVA
 * + perfil y manda el correo de activación. Las EAPs/escuela se referencian por
 * CÓDIGO (intuitivo para el admin); el backend las resuelve dentro de su tenant.
 *
 * No es idempotente: una fila cuyo correo/documento ya existe se reporta como
 * error de esa fila (no aborta el lote), no se re-crea.
 */

const documento = z.string().trim().min(6, "Documento muy corto").max(20);
const nombre = z.string().trim().min(1).max(120);
const correo = z.string().trim().toLowerCase().email("Correo inválido").max(255);
const codigoEscuela = z.string().trim().min(1).max(40);

/** Una fila de docente. `escuelaCodigos` = EAPs a las que se afilia (0+). */
export const filaImportarDocenteSchema = z.object({
  documentoIdentidad: documento,
  nombres: nombre,
  apellidos: nombre,
  correo,
  escuelaCodigos: z.array(codigoEscuela).max(20).optional(),
});
export type FilaImportarDocenteInput = z.infer<typeof filaImportarDocenteSchema>;

/** Una fila de estudiante. `escuelaCodigo` (su EAP) es obligatorio. */
export const filaImportarEstudianteSchema = z.object({
  documentoIdentidad: documento,
  nombres: nombre,
  apellidos: nombre,
  correo,
  escuelaCodigo: codigoEscuela,
});
export type FilaImportarEstudianteInput = z.infer<typeof filaImportarEstudianteSchema>;

const MAX_FILAS = 1000;

export const importarDocentesSchema = z.object({
  archivoNombre: z.string().trim().max(255).optional(),
  filas: z
    .array(filaImportarDocenteSchema)
    .min(1, "Debes incluir al menos una fila")
    .max(MAX_FILAS, `Máximo ${MAX_FILAS} filas por importación`),
});
export type ImportarDocentesInput = z.infer<typeof importarDocentesSchema>;

export const importarEstudiantesSchema = z.object({
  archivoNombre: z.string().trim().max(255).optional(),
  filas: z
    .array(filaImportarEstudianteSchema)
    .min(1, "Debes incluir al menos una fila")
    .max(MAX_FILAS, `Máximo ${MAX_FILAS} filas por importación`),
});
export type ImportarEstudiantesInput = z.infer<typeof importarEstudiantesSchema>;

/** Error de una fila que no se pudo importar (no aborta el lote). */
export interface ErrorFilaImportacion {
  /** Índice 1-based de la fila en el archivo. */
  fila: number;
  documento: string | null;
  motivo: string;
}

/** Resultado de una corrida de import: cuántas cuentas se crearon + invitaron. */
export interface ResultadoImportacionPlantaWire {
  totalFilas: number;
  creados: number;
  errores: ErrorFilaImportacion[];
}
